import React, { useEffect, useState, useMemo } from 'react';
import { useCartContext } from './CartContext';
import Image from '@components/atoms/Image';
import Button from '@components/atoms/Button';
import { formatMoney, handleAddToCart } from '@helpers/global';
import CartRecommendationsSkeleton from './CartRecommendationsSkeleton';
import { trackAddToCart } from '@helpers/trackCartActions';
import { RelatedVariant } from '@interfaces/cart';

interface CartRecommendationsProps {
  isCartPage: boolean;
}

interface RecommendedProduct {
  source: 'relatedVariants' | 'shopifyRecommendations';
  availableForSale: boolean;
  title: string;
  type: string;
  vendor: string;
  sku: string;
  image: string;
  price: number;
  handle: string;
  variantId: string;
}

interface SimplifiedShopifyProduct {
  title: string;
  type: string;
  vendor: string;
  handle: string;
  price: number;
  featured_image?: string;
  variants: Array<{
    sku: string
    available?: boolean;
    id?: number;
    featured_image?: {
      src: string;
    };
  }>;
}

const CartRecommendations: React.FC<CartRecommendationsProps> = ({ isCartPage }) => {
  const { cart } = useCartContext();
  const [recommendedProducts, setRecommendedProducts] = useState<RecommendedProduct[]>([]);
  const [loading, setLoading] = useState(true);
  const MAX_RECOMMENDATIONS = 3;

  // Helper to get cart variant IDs
  const getCartVariantIds = () =>
    cart.state.lines?.map((line) => line.merchandise?.id).filter((id): id is string => id !== undefined) || [];

  // Fetch Shopify recommendations
  const fetchShopifyRecommendations = async (productIds: string[]): Promise<RecommendedProduct[]> => {
    const limit = productIds.length === 1 ? 6 : 3
    const responses = await Promise.all(
      productIds.map(async (productId) => {
        const response = await fetch(`/recommendations/products.json?product_id=${productId}&limit=${limit}`);
        if (response.ok) {
          const data = await response.json();
          return data.products
            .filter((product: SimplifiedShopifyProduct) => product.vendor !== "SavedBy")
            .map((product: SimplifiedShopifyProduct) => ({
              source: 'shopifyRecommendations',
              availableForSale: product.variants[0]?.available || false,
              title: product.title,
              type: product.type,
              vendor: product.vendor,
              sku: product.variants[0]?.sku ? product.variants[0]?.sku : product.variants[0]?.id,
              image: product.variants[0]?.featured_image?.src || product.featured_image,
              price: product.price / 100,
              handle: product.handle,
              variantId: product.variants[0]?.id ? `gid://shopify/ProductVariant/${product.variants[0]?.id}` : '',
            }));
        }
        return [];
      })
    );
    return responses.flat();
  };

  // Main function to fetch recommendations
  const fetchRecommendations = async () => {
    try {
      if (!cart.state.lines || cart.state.lines.length === 0) {
        setLoading(false);
        return;
      }
  
      const cartVariantIds = getCartVariantIds(); // Extract variant IDs from the cart
      const recommendations: RecommendedProduct[] = [];
      const productIds = cart.state.lines
        .map((line) => line.merchandise.product.id.split('/').pop())
        .filter((id): id is string => id !== undefined);
  
      // Iterate over cart lines to extract related variants
      cart.state.lines.forEach((line) => {
        // Parse the `__internal_relatedVariants` attribute (if it exists and is valid JSON)
        const relatedVariantsRaw = line.attributes?.find(
          (attr) => attr.key === '__internal_relatedVariants'
        )?.value;
  
        let relatedVariants: RelatedVariant[] = [];
        if (relatedVariantsRaw && relatedVariantsRaw !== 'null') {
          try {
            relatedVariants = JSON.parse(relatedVariantsRaw);
          } catch (error) {
            console.warn('Failed to parse relatedVariants:', error);
          }
        }
  
        relatedVariants.forEach((variant: RelatedVariant) => {
          if (
            variant.availableForSale &&
            !cartVariantIds.includes(variant.variantId) &&
            !recommendations.some((r) => r.variantId === variant.variantId)
          ) {
            recommendations.push({
              source: 'relatedVariants',
              availableForSale: variant.availableForSale,
              title: variant.title,
              type: variant.type,
              vendor: variant.vendor,
              sku: variant.sku,
              image: variant.image || '',
              price: variant.price,
              handle: variant.handle,
              variantId: variant.variantId,
            });
          }
        });
      });
  
      // Fill with Shopify fallback recommendations if necessary
      if (recommendations.length < MAX_RECOMMENDATIONS) {
        const fallbackRecommendations = await fetchShopifyRecommendations(productIds);
        fallbackRecommendations.forEach((product) => {
          if (
            recommendations.length < MAX_RECOMMENDATIONS &&
            product.availableForSale &&
            !cartVariantIds.includes(product.variantId) &&
            !recommendations.some((r) => r.variantId === product.variantId)
          ) {
            recommendations.push(product);
          }
        });
      }
  
      // Update state with recommendations (limited to MAX_RECOMMENDATIONS)
      setRecommendedProducts(recommendations.slice(0, MAX_RECOMMENDATIONS));
      setLoading(false);
    } catch (error) {
      console.error('Error fetching recommendations:', error);
      setLoading(false);
    }
  };

  // Handle Add to Cart
  const handleAddToCartClick = (product: RecommendedProduct) => {
    handleAddToCart(product.variantId);

    // Track the add_to_cart event
    trackAddToCart(
      {
        title: product.title,
        vendor: product.vendor,
        type: product.type,
      },
      {
        sku: product.variantId,
        price: product.price.toString(),
      },
    );

    // Remove the product from recommendations after adding to cart
    setRecommendedProducts((prev) => prev.filter((prevProduct) => prevProduct.variantId !== product.variantId));
  };

  useEffect(() => {
    fetchRecommendations();
  }, [cart.state.lines, ]);

  const formattedRecommendations = useMemo(() => recommendedProducts, [recommendedProducts]);

  if (loading) {
    return <CartRecommendationsSkeleton isCartPage={isCartPage} />;
  }

  if (!formattedRecommendations.length) return null;

  return (
    <div className={`${isCartPage ? 'px-2.5 mt-8 md:px-1' : 'px-2.5 mt-2'}`}>
      <div className="px-1">
        <h3 className={`mb-4 text-black ${isCartPage ? 'h3' : 'text-24 leading-33 font-appleGaramond font-light'}`}>
          Enhance your package
        </h3>
        <div className={`grid grid-cols-1 gap-4 ${isCartPage ? 'md:grid-cols-3 md:gap-8' : ''}`}>
          {formattedRecommendations.map((product) => (
            <div
              className={`w-full flex items-center gap-4 ${isCartPage ? 'md:items-start md:flex-col' : ''}`}
              key={product.variantId}
              role="group"
              aria-label="recommended product not in cart"
            >
              <div className={`w-15 h-15 flex justify-center items-center bg-white rounded shadow-md ${isCartPage ? 'md:w-full md:h-full' : ''}`}>
                <a href={`/products/${product.handle}`} aria-label={`Link to the recommended product ${product.title}`}>
                  <Image
                    src={product.image}
                    alt={product.title}
                    classes="w-full h-full object-contain rounded aspect-square"
                    srcWidths={{ sm: 304, md: 304, lg: 304, xl: 304, '2xl': 304 }}
                    blendMode="multiply"
                  />
                </a>
              </div>
              <div
                className={`flex-1 flex gap-2 justify-between items-center ${
                  isCartPage ? 'md:flex-col md:items-start' : ''
                }`}
              >
                <a href={`/products/${product.handle}`}>
                  <div className="flex flex-col">
                    <h4 className="body-xxs font-bold">{product.title}</h4>
                    <p className="body-sm font-semibold">
                      {formatMoney({
                        amount: product.price,
                        currencyCode: window.DEPT.localization.currencyCode,
                      }) || `$${product.price}`}
                    </p>
                  </div>
                </a>
                <Button
                  mode="button"
                  buttonText="Add to cart"
                  onClick={() => handleAddToCartClick(product)}
                  className="min-w-22.5 min-h-7.5 max-h-7.5 button--sm"
                />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default CartRecommendations;
